import styled from 'styled-components'
import { Box } from '../UI/Box'
import { P } from '../Library/Typeography'
import routes from '../../router/routes'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { selectBottomNavButton } from '../../modules/miscUi/selectors'
import {
  AcknowledgeSeasonStartButton,
  BackToSeasonButton,
  useClearButtonOnNavigate,
} from '../../page_components/Home/Season/About'
import { AcknowledgeQuestCompleteButton } from '../../page_components/Home/Season/QuestComplete'
import {
  AcknowledgeSeasonLossButton,
  ClaimSeasonPrizesButton,
} from '../../page_components/Home/Season/SeasonComplete'
import useWeb3 from '../../hooks/useWeb3'
import useIsLoggedIn from '../../hooks/useIsLoggedIn'
import { useShouldShowForThreshold } from '../../theme'
import { selectClaimPrizeTxHash } from '../../modules/seasons/selectors'

const MenuItemLabel = {
  Explore: 'Explore',
  Seasons: 'Seasons',
  Account: 'Account',
}

const Menu = [
  {
    title: MenuItemLabel.Explore,
    iconPath: 'explore.svg',
    link: routes.home,
    activeOnLinks: [/(?<=\/launch).*/],
  },
  {
    title: MenuItemLabel.Seasons,
    iconPath: 'seasons.svg',
    link: routes.season.index,
    activeOnLinks: [/^\/season(\/.*)?$/],
  },
  {
    title: MenuItemLabel.Account,
    iconPath: 'you.svg',
    link: routes.myzap.tokensHeld,
    activeOnLinks: [/^\/myzap\/*/],
  },
]

export const BottomMenu = () => {
  const { pathname } = useLocation()
  const { push } = useHistory()
  const { initWeb3 } = useWeb3()
  const { isLoggedIn } = useIsLoggedIn()

  const handleClick = (link: string) => () => {
    push(link)
  }

  const bottomNavButton = useSelector(selectBottomNavButton)
  const hasExtendedBottomNav = !!useSelector(selectClaimPrizeTxHash)

  return (
    <BottomContainer
      hasActionButton={!!bottomNavButton}
      hideBottom={false}
      hasExtendedBottomNav={hasExtendedBottomNav}
    >
      <Box dc g={24}>
        {!!bottomNavButton && (
          <ButtonNavActionButton buttonType={bottomNavButton} />
        )}
        <Box ac jc g={24}>
          {Menu.map(({ title, iconPath, link, activeOnLinks }) => {
            const isActive =
              pathname === link ||
              activeOnLinks.some((regex) => regex.test(pathname))
            const shouldInitWeb3 =
              title === MenuItemLabel.Account && !isLoggedIn
            const onClick = shouldInitWeb3 ? initWeb3 : handleClick(link)
            return (
              <Item onClick={onClick}>
                <img
                  src={`/img/icons/sidebar/${isActive ? 'filled/' : ''}${iconPath}`}
                  alt={title}
                />
                <P 
                  s="XS"
                  fw={isActive ? 'semibold' : 'regular'}
                  c={isActive ? 'text-primary' : 'text-secondary'}
                >
                  {title}
                </P>
              </Item>
            )
          })}
        </Box>
      </Box>
    </BottomContainer>
  )
}

// NOTE: devs, real simple, we just push the key of your button to redux
// and render accordingly, then we can have all sorts of complicated logic
export function ButtonNavActionButton({
  buttonType,
  modalHeight,
  scrollAmountRemaining,
  modalRef,
}: {
  buttonType: string
  modalHeight?: number
  scrollAmountRemaining?: number
  modalRef?: React.RefObject<HTMLDivElement>
}) {
  useClearButtonOnNavigate()
  const isAtLeastMediumScreen = useShouldShowForThreshold('medium')
  const showBottomWrapper = !!modalHeight && isAtLeastMediumScreen

  function continueScrolling() {
    if (!modalRef?.current) return
    modalRef.current?.scrollBy({
      top: modalHeight,
      behavior: 'smooth',
    })
  }

  const Wrapper = ({ children }) =>
    showBottomWrapper ? (
      <ModalBottomButtonWrapper>{children}</ModalBottomButtonWrapper>
    ) : (
      <>{children}</>
    )

  return (
    <Wrapper>
      {buttonType === 'acknowledge_season_start' && (
        <AcknowledgeSeasonStartButton
          continueScrolling={continueScrolling}
          moreToScroll={!!scrollAmountRemaining}
        />
      )}
      {buttonType === 'back_to_season' && (
        <BackToSeasonButton
          continueScrolling={continueScrolling}
          moreToScroll={!!scrollAmountRemaining}
        />
      )}
      {buttonType === 'acknowledge_quest_complete' && (
        <AcknowledgeQuestCompleteButton
          continueScrolling={continueScrolling}
          moreToScroll={!!scrollAmountRemaining}
        />
      )}
      {buttonType === 'claim_season_prizes' && (
        <ClaimSeasonPrizesButton
          continueScrolling={continueScrolling}
          moreToScroll={!!scrollAmountRemaining}
        />
      )}
      {buttonType === 'acknowledge_season_end' && (
        <AcknowledgeSeasonLossButton
          continueScrolling={continueScrolling}
          moreToScroll={!!scrollAmountRemaining}
        />
      )}
    </Wrapper>
  )
}

const ModalBottomButtonWrapper = styled(Box)`
  position: sticky;
  z-index: 42069;
  bottom: -1px;
  margin-left: -32px;
  border-top: 1px solid var(--stroke-secondary, #333);
  align-self: stretch;
  padding: 16px;
  width: calc(100% + 64px);
  background: linear-gradient(
      0deg,
      var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 0%,
      var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 100%
    ),
    var(--surface-primary, #080808);
`

export const TopMenu = () => {
  const { pathname } = useLocation()
  const { push } = useHistory()

  const { initWeb3 } = useWeb3()
  const { isLoggedIn } = useIsLoggedIn()

  const handleClick = (link: string) => () => {
    push(link)
  }

  return (
    <Box ac jc g={8}>
      {Menu.map(({ title, link, activeOnLinks }) => {
        const isActive =
          pathname === link ||
          activeOnLinks.some((regex) => regex.test(pathname))
        const shouldInitWeb3 = title === MenuItemLabel.Account && !isLoggedIn
        const onClick = shouldInitWeb3 ? initWeb3 : handleClick(link)
        return (
          <Label
            s="L"
            onClick={onClick}
            fw={isActive ? 'semibold' : 'regular'}
            c={isActive ? 'text-primary' : 'text-secondary'}
            isActive={isActive}
            key={title}
          >
            {title}
          </Label>
        )
      })}
    </Box>
  )
}

const Label = styled(P)<{ isActive: boolean }>`
  display: flex;
  padding: var(--units-12, 12px);
  justify-content: center;
  align-items: center;
  gap: var(--units-8, 8px);
  border-radius: var(--units-88, 88px);

  ${({ isActive }) =>
    isActive &&
    `
    cursor: default;
  `}

  ${({ isActive }) =>
    !isActive &&
    `
    :hover {
      cursor: pointer;
      color: var(--text-primary, #FFF);
      background: var(--states-minimal-alpha-4, rgba(255, 255, 255, 0.04));
    }
  `}
`

const Item = styled.div`
  display: flex;
  width: 60px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`

const BottomContainer = styled(Box)<{
  hasActionButton: boolean
  hideBottom: boolean
  hasExtendedBottomNav: boolean
}>`
  position: fixed;
  height: ${({ hasActionButton, hideBottom, hasExtendedBottomNav }) =>
    hasActionButton
      ? hideBottom
        ? 132
        : hasExtendedBottomNav
          ? 69 + 117
          : 146
      : 74}px;
  padding: var(--units-16, 16px) 16px;
  bottom: 0;
  z-index: 42069;
  border-top: 1px solid #252525;
  background: linear-gradient(
      0deg,
      var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 0%,
      var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 100%
    ),
    var(--surface-primary, #080808);
`
